import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex items-center p-6 border-b border-gray-200 bg-navy-blue text-white">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mr-4 text-blue-500 hover:text-gray-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold">Privacy Policy</h1>
          </div>
          
          <div className="p-6">
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold text-navy-blue mb-6">Privacy Notice</h2>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Introduction</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Catch of the Day is dedicated to safeguarding the privacy and confidentiality of your personal information 
                  in compliance with Bermuda's Personal Information Protection Act (PIPA). As a Customer of our mobile application, 
                  your privacy is of utmost importance to us. We are committed to transparently outlining how your information is 
                  collected, used, and protected.
                </p>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Information Collection</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  The information collected from Customers is primarily used to enhance your experience on the Catch of the Day app. 
                  This includes but is not limited to:
                </p>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                  <li className="leading-relaxed">
                    <strong>Individual Store Orders:</strong> The app may display a large dataset of individual store orders that you have placed, 
                    facilitating easy access to your order history and preferences.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Feedback:</strong> Your feedback on the app's features, usability, and your overall experience is 
                    collected to improve our services continually.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Location Data:</strong> Catch of the Day may collect location data to provide you with relevant information 
                    about nearby feeding centres, volunteering opportunities, and available deals at local stores.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Dietary Preferences:</strong> Your dietary preferences are used to personalize your browsing experience, 
                    ensuring that you are presented with relevant food options that align with your dietary restrictions or preferences.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Selection Choices:</strong> Information about the selections you make within the app, such as opting to volunteer, 
                    donating to a charity, or reviewing food, helps us tailor our services to better meet your needs and interests.
                  </li>
                  <li className="leading-relaxed">
                    <strong>Environmental Impact:</strong> Catch of the Day may calculate and display the amount of greenhouse gases saved 
                    by Customers through their use of the application, promoting awareness of the environmental benefits of reducing food waste.
                  </li>
                </ol>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Disclosure of Personal Information</h3>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700" start={7}>
                  <li className="leading-relaxed">
                    Personal information may be disclosed to authorized individuals within Catch of the Day, as well as to 
                    subcontractors such as SpectroPay, for payment processing purposes.
                  </li>
                  <li className="leading-relaxed">
                    Businesses may access the telephone number and the email of an order whilst a customer has placed an order 
                    into our system. This information will elapse after the order is completed or refunded.
                  </li>
                  <li className="leading-relaxed">
                    Businesses may access end-user and charity information to facilitate donations and track compliance 
                    with food redistribution policies.
                  </li>
                  <li className="leading-relaxed">
                    Charities may access business and end-user information to coordinate food distribution efforts 
                    and monitor compliance with donation guidelines.
                  </li>
                </ol>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Storage and Protection of Personal Information</h3>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700" start={11}>
                  <li className="leading-relaxed">
                    Personal information is stored securely and access is restricted to authorized personnel only.
                  </li>
                  <li className="leading-relaxed">
                    We take all reasonable measures to protect personal information from unauthorized access, disclosure, 
                    alteration, or destruction.
                  </li>
                </ol>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Retention and Erasure of Personal Information</h3>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700" start={13}>
                  <li className="leading-relaxed">
                    Personal information collected for food redistribution purposes will be retained for a maximum of 7 years, 
                    after which it will be securely erased.
                  </li>
                  <li className="leading-relaxed">
                    Personal information provided to Charities regarding applicants that wish to volunteer will only be kept 
                    in our database for 30 days. Within that timeframe the charity will need to store the information, 
                    retain the information for themselves before contacting the applicant.
                  </li>
                  <li className="leading-relaxed">
                    Catch of the Day is not liable for any personal information stored by charities or organizations that have 
                    received such information from applicants wishing to volunteer their services. While we strive to ensure 
                    the secure handling of personal data within our platform, we cannot guarantee the security of information 
                    once it has been shared with external parties. It is the responsibility of charities and organizations to 
                    adhere to data protection regulations and safeguard the privacy of individuals' personal information.
                  </li>
                  <li className="leading-relaxed">
                    Payment information and all transactions are stored by SpectroPay will be subject to their retention 
                    and erasure policies.
                  </li>
                </ol>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Contact Information</h3>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700" start={17}>
                  <li className="leading-relaxed">
                    For inquiries or concerns regarding the handling of personal information, individuals may contact our 
                    Privacy Protection Officer, Aymori Duncan, at <a href="mailto:daymori@catchoftheday.io" className="text-blue-600 hover:underline">daymori@catchoftheday.io</a>.
                  </li>
                  <li className="leading-relaxed">
                    SpectroPay may also be contacted directly at <a href="mailto:info@spectropay.bm" className="text-blue-600 hover:underline">info@spectropay.bm</a> or 
                    <a href="tel:+14414568628" className="text-blue-600 hover:underline"> +1 (441) 456 8628</a>.
                  </li>
                </ol>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-sky-blue mb-3">Consent and Rights</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  By using Catch of the Day, businesses, charities, and individual users consent to the collection and use of 
                  personal information as outlined in this notice. Users have the right to access, correct, or delete their 
                  personal information, and may contact our Privacy Protection Officer for assistance with these requests.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  This privacy notice is subject to change, and any updates will be communicated through our platform.
                </p>
              </div>
              
              <div className="text-center text-gray-500 text-sm mt-12">
                Last updated: {new Date().toISOString().split('T')[0]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
